import { Group, Loader, Modal, Stack } from '@mantine/core';
import { Button, Icon, Text } from '@repo/foundations';
import { IconExternalLink } from '@tabler/icons-react';
import { useBackgroundJob } from '../../../api/hooks/backgroundJob';

interface ImportResultsDialogProps {
	opened: boolean;
	onClose: () => void;
	resultsFileUrl?: string | null;
	jobId?: string;
}

export function ImportResultsDialog({
	opened,
	onClose,
	resultsFileUrl: initialResultsFileUrl,
	jobId,
}: ImportResultsDialogProps) {
	// Poll the background job for completion and updated results file URL
	const { data: backgroundJob } = useBackgroundJob({
		id: jobId || '',
		options: {
			enabled: !!jobId && opened,
			refetchInterval: (query) => {
				const { data } = query.state;
				if (data?.completed || data?.failed) {
					return false; // Stop polling when job is complete
				}
				return 2_500; // Poll every 2.5 seconds
			},
		},
	});

	// Use the initial results file URL if available
	const resultsFileUrl = initialResultsFileUrl;
	const isJobComplete = backgroundJob?.completed || backgroundJob?.failed;
	const isJobFailed = backgroundJob?.failed;

	const handleDownloadResults = () => {
		if (resultsFileUrl) {
			window.open(resultsFileUrl, '_blank');
		}
	};

	const getModalTitle = () => {
		if (isJobFailed) return 'Import Failed';
		if (isJobComplete) return 'Import Processed';
		return 'Import in Progress';
	};

	const parseImportStats = (logs?: string) => {
		if (!logs) return { successful: 0, total: 0 };

		// Directly match the background job log message that has the import stats
		const match = logs.match(
			/Finished bulk import: Successfully imported (\d+) out of (\d+) rows/
		);

		return match
			? { successful: parseInt(match[1]), total: parseInt(match[2]) }
			: { successful: 0, total: 0 };
	};

	const getStatusMessage = () => {
		if (isJobFailed) {
			return (
				<Text size="sm" color="text/critical/default">
					Your bulk import has failed. Please check the job details and try
					again.
				</Text>
			);
		}
		if (isJobComplete) {
			const importStats = parseImportStats(backgroundJob?.logs);

			return (
				<Text size="sm" color="text/secondary/default">
					Your bulk import has been processed. There were{' '}
					{importStats?.successful} successful imports out of{' '}
					{importStats?.total} total rows. Please download the results file to
					see the import details and any errors that occurred.
				</Text>
			);
		}
		return (
			<Text size="sm" color="text/secondary/default">
				Your bulk import is currently being processed. This may take a few
				minutes. The results will be available once the import is complete.
			</Text>
		);
	};

	const getResultsSection = () => {
		if (resultsFileUrl && isJobComplete && !isJobFailed) {
			return (
				<Group>
					<Button onClick={handleDownloadResults} variant="primary">
						<Icon name="download" />
						Download Results
					</Button>
					<Button onClick={handleDownloadResults} variant="tertiary">
						<IconExternalLink size={16} />
						Open in New Tab
					</Button>
				</Group>
			);
		}
		if (!isJobComplete && !isJobFailed) {
			return (
				<>
					<Text size="sm" color="text/warning/default">
						A results file will be available once the import is complete.
					</Text>
					<Loader size="sm" />
				</>
			);
		}
		if (isJobFailed) return null;
		return (
			<Text size="sm" color="text/warning/default">
				Results file is not yet available. Please try again in a few minutes.
			</Text>
		);
	};

	return (
		<Modal opened={opened} onClose={onClose} title={getModalTitle()} size="md">
			<Stack spacing="md">
				{getStatusMessage()}
				{getResultsSection()}
				{jobId && (
					<Text size="xs" color="text/secondary/default">
						Job ID: {jobId}
					</Text>
				)}
			</Stack>
		</Modal>
	);
}
